import QS from "qs";
import store from "../store";
// eslint-disable-next-line valid-jsdoc
/**
 * JsonApiFilterManager class.
 *
 * This class manages the building and formatting of JsonApi query filters.
 *
 * @type {{queries: {include: string, "filter[publishingStatus][condition][path]": string, "filter[publishingStatus][condition][value]": string,
 * "filter[operativeStatus][condition][path]": string, "filter[operativeStatus][condition][value]": string,
 * "fields[chemdb_manufacturer--chemdb_manufacturer]": string, "fields[node--chemdb_type]": string, "fields[node--laboratory]": string,
 * "fields[node--space]": string, "fields[node--department]": string, "fields[node--building]": string, "fields[node--campus]": string,
 * sort: string}, setQueriesFromQueryString(), getQueries(): *, setPageOffset(*): *, setPageLimit(*): *, setSortFields(*): *, setIncludes(*): *,
 * addField(*, *): *, addFilter(*, *, *, *=): *, addMultivalueFilter(*, *, *, *=): *, addMultivalueNamedFilter(*, *, *, *=): void, deleteFiltersByName(*): void,
 * getFilterFromUrl(*=): *}}
 */
export const JsonApiFilterManagerFactory = function () {
  return {
    /**
     * Object representing JsonApi query filters.
     *
     * @type {Object}
     */
    queries: {
      include:
        "field_chemical_type,og_audience,field_chemical_space,field_chemical_manufacturer,og_audience.og_audience,field_chemical_space.field_parent_location,field_chemical_space.field_parent_location.field_parent_location,field_chemical_sds,field_chemical_sds.field_chemdb_type_related_file,field_chemical_container_owner",
      "filter[publishingStatus][condition][path]": "status",
      "filter[publishingStatus][condition][value]": "1",
      "filter[operativeStatus][condition][path]": "operative_status",
      "filter[operativeStatus][condition][value]": "1",
      "fields[chemdb_manufacturer--chemdb_manufacturer]": "id,name",
      "fields[node--chemdb_type]":
        "id,drupal_internal__nid,title,field_chemdb_cas_number,field_chemdb_physical_state",
      "fields[node--laboratory]": "drupal_internal__nid,title,og_audience",
      "fields[node--space]": "drupal_internal__nid,title,field_parent_location",
      "fields[node--department]": "drupal_internal__nid,title",
      "fields[node--building]":
        "drupal_internal__nid,title,field_parent_location",
      "fields[node--campus]": "drupal_internal__nid,title",
      "fields[user--user]": "drupal_internal__uid,field_prof_display_name",
      sort: "field_chemical_name_preferred,-created",
    },

    /**
     * Set all the query filters from the query string.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    setQueryFiltersFromQueryString() {
      // TODO: use the getFilterFromUrl() method to populate this.queries.
      const uri = window.location.search.substring(1);
      if (!uri.includes("filter")) {
        return this;
      }
      const params = QS.parse(uri);
      for (const property in params.filter) {
        if (params.filter.hasOwnProperty(property)) {
          if (params.filter[property].storeKey) {
            store.commit(
              params.filter[property].storeKey,
              JSON.parse(params.filter[property].storeValue),
            );
          }
        }
      }
      this.setUpdateFilters();

      return this;
    },

    /**
     * Get all the query filters.
     *
     * @return {Object} - Returns an object representing the query filters.
     */
    getQueryFilters() {
      return this.queries;
    },

    /**
     * Set the query filter page offset.
     *
     * @param {Number} offset - Integer representing the query filter page offset.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    setPageOffset(offset) {
      this.queries["page[offset]"] = parseInt(offset, 10);
      this.setUpdateFilters();
      return this;
    },

    /**
     * Set the query page limit to control the number of records to fetch.
     *
     * @param {Number} limit - Integer representing the number of records to fetch.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    setPageLimit(limit) {
      this.queries["page[limit]"] = parseInt(limit, 10);
      this.setUpdateFilters();
      return this;
    },

    /**
     * Set the fields to sort by.
     *
     * @param {String} sort - String representing field(s) to sort by.  Multiple fields are separated by comma.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    setSortFields(sort) {
      this.queries.sort = sort;
      this.setUpdateFilters();
      return this;
    },

    /**
     * Set the JsonApi includes.
     *
     * @param {String} includes - String representing the include list for JsonApi.  Multiple includes are separated by comma.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    setIncludes(includes) {
      this.queries.include = includes;
      this.setUpdateFilters();
      return this;
    },

    /**
     * Add a field to include in the results.
     *
     * @param {String} bundle - String representing the name of the bundle (content-type).
     * @param {String} fields - String representing the comma-separated list of fields on a given bundle.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addField(bundle, fields) {
      this.queries["fields[" + bundle + "]"] = fields;
      this.setUpdateFilters();
      return this;
    },

    /**
     * Add a field to include in the results.
     *
     * @param {String} name - String representing the name of the bundle (content-type).
     * @param {String} conjunction - String representing the comma-separated list of fields on a given bundle.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addFilterGroup(name, conjunction) {
      this.queries["filter[" + name + "][group][conjunction]"] = conjunction;
      this.setUpdateFilters();
      return this;
    },

    /**
     * Add a basic filter.
     *
     * @param {String} name - String representing the name of the filter.
     * @param {String} path - String representing the filter path.
     * @param {String} value - String representing the filter value.
     * @param {String} operator - String representing the filter operator.
     * @param {String} storeKey - String representing the store commit key.
     * @param {Object} storeValue - String representing the store commit key.
     * @param {Object} memberOf - String representing the jsonapi memberOf filter group.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addFilter(
      name,
      path,
      value,
      operator = "=",
      storeKey = "",
      storeValue = "",
      memberOf = "",
    ) {
      this.queries["filter[" + name + "][condition][path]"] = path;
      this.queries["filter[" + name + "][condition][value]"] = value;
      this.queries["filter[" + name + "][condition][operator]"] = operator;
      if (storeKey) {
        this.queries["filter[" + name + "][storeKey]"] = storeKey;
        this.queries["filter[" + name + "][storeValue]"] =
          JSON.stringify(storeValue);
      }
      if (memberOf) {
        this.queries["filter[" + name + "][condition][memberOf]"] = memberOf;
      }
      this.setUpdateFilters();
      return this;
    },

    addNullFilter(name, path) {
      this.queries["filter[" + name + "][condition][path]"] = path;
      this.queries["filter[" + name + "][condition][operator]"] = "IS NULL";
      this.setUpdateFilters();
      return this;
    },

    addNotNullFilter(name, path) {
      this.queries["filter[" + name + "][condition][path]"] = path;
      this.queries["filter[" + name + "][condition][operator]"] = "IS NOT NULL";
      this.setUpdateFilters();
      return this;
    },

    /**
     * Add a multi-value filter.
     *
     * @param {String} name - String representing the name of the filter.
     * @param {String} path - String representing the filter path.
     * @param {Array} value - Array representing the filter values.
     * @param {String} storeKey - String representing the store commit key.
     * @param {Object} storeValue - String representing the store commit key.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addMultivalueStartsWithFilter(
      name,
      path,
      value,
      storeKey = "",
      storeValue = "",
    ) {
      this.queries["filter[" + name + "][group][conjunction]"] = "OR";

      if (storeKey) {
        this.queries["filter[" + name + "][storeKey]"] = storeKey;
        this.queries["filter[" + name + "][storeValue]"] =
          JSON.stringify(storeValue);
      }
      for (let i = 0, len = value.length; i < len; i++) {
        const label = name + "_" + i;
        this.queries["filter[" + label + "][condition][operator]"] =
          "STARTS_WITH";
        this.queries["filter[" + label + "][condition][path]"] = path;
        this.queries["filter[" + label + "][condition][memberOf]"] = name;
        this.queries["filter[" + label + "][condition][value]"] = value[i];
      }
      this.setUpdateFilters();
    },

    /**
     * Add a multi-value filter.
     *
     * @param {String} name - String representing the name of the filter.
     * @param {String} path - String representing the filter path.
     * @param {Array} value - Array representing the filter values.
     * @param {String} storeKey - String representing the store commit key.
     * @param {Object} storeValue - String representing the store commit key.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addMultivalueNamedFilter(
      name,
      path,
      value,
      storeKey = "",
      storeValue = "",
    ) {
      this.queries["filter[" + name + "][condition][path]"] = path;
      this.queries["filter[" + name + "][condition][operator]"] = "IN";
      if (storeKey) {
        this.queries["filter[" + name + "][storeKey]"] = storeKey;
        this.queries["filter[" + name + "][storeValue]"] =
          JSON.stringify(storeValue);
      }
      for (let i = 0, len = value.length; i < len; i++) {
        this.queries["filter[" + name + "][condition][value][" + i + "]"] =
          value[i];
      }
      this.setUpdateFilters();
    },

    /**
     * Delete filters.
     *
     * @param {Array} filters - Array representing filters to delete.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    deleteFiltersByName(filters) {
      for (let i = 0, len = filters.length; i < len; i++) {
        const search = "filter[" + filters[i];
        for (const key in this.queries) {
          if (key.indexOf(search) === 0) {
            delete this.queries[key];
          }
        }
      }
      this.setUpdateFilters();
      return this;
    },

    /**
     * Extract a param from the query string.
     *
     * @param {String} param - String representing a parameter in the query string.
     *
     * @return {string | null} - Returns the value from the query string, or null if not found.
     */
    getFilterFromUrl(param) {
      const uri = window.location.search.substring(1);
      const params = new URLSearchParams(uri);
      return params.get(param);
    },

    setUpdateFilters() {
      store.commit("setJsonApiFilters", {});
      store.commit("setJsonApiFilters", this.queries);
    },

    /**
     * Add a multi-value filter.
     *
     * @param {String} name - String representing the name of the filter.
     * @param {String} path - String representing the filter path.
     * @param {Object} value - Object representing the filter values.
     * @param {String} storeKey - String representing the store commit key.
     * @param {Object} storeValue - String representing the store commit key.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addMultiValueContainsAndFilter(
      name,
      path,
      value,
      storeKey = "",
      storeValue = "",
    ) {
      this.queries["filter[" + name + "][group][conjunction]"] = "AND";

      if (storeKey) {
        this.queries["filter[" + name + "][storeKey]"] = storeKey;
        this.queries["filter[" + name + "][storeValue]"] =
          JSON.stringify(storeValue);
      }
      for (let i = 0, len = value.length; i < len; i++) {
        const label = name + "_" + i;
        this.queries["filter[" + label + "][condition][operator]"] = "CONTAINS";
        this.queries["filter[" + label + "][condition][path]"] = path;
        this.queries["filter[" + label + "][condition][memberOf]"] = name;
        this.queries["filter[" + label + "][condition][value]"] = value[i];
      }
      this.setUpdateFilters();
    },

    /**
     * Add a multi-value filter.
     *
     * @param {String} name - String representing the name of the filter.
     * @param {String} path - String representing the filter path.
     * @param {Array} value - Array representing the filter values.
     * @param {String} storeKey - String representing the store commit key.
     * @param {Object} storeValue - String representing the store commit key.
     * @param {String} group - String representing the JSONAPI or group name.
     *
     * @return {JsonApiFilterManager} - Provides a fluent interface.
     */
    addMultivalueNamedFilterWithGroupOr(
      name,
      path,
      value,
      storeKey = "",
      storeValue = "",
      group = "",
    ) {
      this.queries["filter[" + group + "][group][conjunction]"] = "OR";

      this.queries["filter[" + name + "][condition][path]"] = path;
      this.queries["filter[" + name + "][condition][operator]"] = "IN";
      this.queries["filter[" + name + "][condition][memberOf]"] = group;
      if (storeKey) {
        this.queries["filter[" + name + "][storeKey]"] = storeKey;
        this.queries["filter[" + name + "][storeValue]"] =
          JSON.stringify(storeValue);
      }
      for (let i = 0, len = value.length; i < len; i++) {
        this.queries["filter[" + name + "][condition][value][" + i + "]"] =
          value[i];
      }
      this.setUpdateFilters();
    },
  };
};

const JsonApiFilterManager = JsonApiFilterManagerFactory();

export default JsonApiFilterManager;
